import React, { useEffect, useMemo, useState } from 'react';

import { Confirm, Modal, Select, SpinnerOverlay } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import ProfileTabSection from '@components/common/ProfileTabSection';

import { Icons } from '@icons/icons';

import { useThresholdManagement } from '@hooks/useThresholdManagement';
import { useWindowHeight } from '@hooks/utils/useWindowHeight';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { IPresetThreshold } from '@interfaces/alarms/threshold';
import { TSelectOption } from '@interfaces/components/componentTypes';

import {
	EAdminPanelPageTitles,
	ESnackbarTypes,
	ETechnologiesLabels,
	ETechnologiesValues,
	EVendorsValues,
	TTechnologiesValues,
	TVendorsValues,
} from '@enums/common';

import { ORAN_5G_TECHNOLOGY } from '@constants/common';
import { responseMessages } from '@constants/responseMessages';

import ThresholdsTechnology from './KpiThresholdsTechnology';

import styles from './KpiThresholdManagement.module.scss';

const OFFSET_HEIGHT = 302;

const KpiThresholdManagement = () => {
	const dispatch = useAppDispatch();

	const [currentTechnology, setCurrentTechnology] = useState<TTechnologiesValues | null>(null);
	const [selectedVendor, setSelectedVendor] = useState<TSelectOption<TVendorsValues> | null>(null);
	const [pendingTechnology, setPendingTechnology] = useState<TTechnologiesValues | null>(null);
	const [pendingVendor, setPendingVendor] = useState<TSelectOption<TVendorsValues> | null>(null);
	const [presetValue, setPresetValue] = useState<TSelectOption<string> & IPresetThreshold | null>(null);

	const [isDirty, setIsDirty] = useState(false);
	const [isError, setIsError] = useState(false);

	const isOranVendor = useMemo(() => selectedVendor?.value === EVendorsValues.oran, [selectedVendor]);

	const {
		createPresetThreshold,
		deletePresetThreshold,
		editOranThresholdsPreset,
		exportThresholds,
		exportOranThresholds,
		isLoadingPresets,
		isLoadingThreshold,
		presetsThreshold,
		renamePresetThreshold,
		thresholds,
		thresholdTechnologies,
		thresholdVendors,
		updateThresholds,
	} = useThresholdManagement({
		vendor: selectedVendor?.value || null,
		technology: currentTechnology,
	});

	const technologyTabs = useMemo(() => {
		if (isOranVendor) {
			const T_5G = thresholdTechnologies.find(t => t === ETechnologiesValues.T_5G);

			return [{
				value: T_5G || thresholdTechnologies[0],
				title: ORAN_5G_TECHNOLOGY || ETechnologiesLabels[thresholdTechnologies[0]],
				isVisible: true,
			}];
		}

		return thresholdTechnologies.map((technology) => ({
			value: technology,
			title: ETechnologiesLabels[technology],
			isVisible: true,
		}));

	}, [thresholdTechnologies, isOranVendor]);

	useEffect(() => {
		if (!thresholdTechnologies.length) {
			return;
		}

		const currentTech = isOranVendor
			? thresholdTechnologies.find(t => t === ETechnologiesValues.T_5G) ?? thresholdTechnologies[0]
			: thresholdTechnologies[0];

		setCurrentTechnology(currentTech);
	}, [thresholdTechnologies, isOranVendor]);

	useEffect(() => {
		if (!thresholdVendors.length) return;

		setSelectedVendor(thresholdVendors.find(vendor => vendor?.isDefault) || null);
	}, [thresholdVendors]);

	const getCurrentTab = () => {
		if (!currentTechnology) return null;

		const onChangePresetValue = (preset: TSelectOption<string> & IPresetThreshold | null) => {
			setPresetValue(preset);

			if (preset?.name) {
				dispatch(setSnackbar({
					message: responseMessages.thresholdManagementPreset(preset.name),
					type: ESnackbarTypes.success,
				}));
			}
		};

		return (
			<ThresholdsTechnology
				createPresetThreshold={createPresetThreshold}
				deletePresetThreshold={deletePresetThreshold}
				exportOranThresholds={exportOranThresholds}
				exportThresholds={exportThresholds}
				isDirty={isDirty}
				isError={isError}
				isLoadingPresets={isLoadingPresets}
				isOranVendor={isOranVendor}
				presetValue={presetValue}
				presetsThreshold={presetsThreshold}
				renamePresetThreshold={renamePresetThreshold}
				setIsDirty={setIsDirty}
				setIsError={setIsError}
				setPresetValue={onChangePresetValue}
				technology={currentTechnology}
				thresholds={thresholds}
				updateOranThresholdPreset={editOranThresholdsPreset}
				updateThresholds={updateThresholds}
				vendor={selectedVendor?.value}
			/>
		);
	};

	const clearPendingTechnology = () => setPendingTechnology(null);

	const clearPendingVendor = () => setPendingVendor(null);

	const onChangeTab = (tab: TTechnologiesValues) => {
		setCurrentTechnology(tab);
		setIsError(false);
		setIsDirty(false);
		setPresetValue(null);

		if (pendingTechnology) clearPendingTechnology();
	};

	const onChangeVendor = (value: TSelectOption<TVendorsValues>) => {
		setSelectedVendor(value);
		setIsError(false);
		setIsDirty(false);
		setPresetValue(null);

		if (pendingVendor) {
			clearPendingVendor();
		}
	};

	const setCurrentVendor = (value: TSelectOption<TVendorsValues>) => {
		if (isDirty) setPendingVendor(value);
		else onChangeVendor(value);
	};

	const setCurrentTab = (tab: TTechnologiesValues) => {
		if (isDirty) setPendingTechnology(tab);
		else onChangeTab(tab);
	};

	const onCloseHandler = () => {
		if (pendingVendor) clearPendingVendor();
		else if (pendingTechnology) clearPendingTechnology();
	};

	const onSubmitHandler = () => {
		if (pendingVendor) onChangeVendor(pendingVendor);
		else if (pendingTechnology) onChangeTab(pendingTechnology);
	};

	const windowHeight = useWindowHeight();

	const [maxHeight, setMaxHeight] = useState('');

	useEffect(() => {
		const maxHeight = windowHeight - OFFSET_HEIGHT;

		setMaxHeight(`${maxHeight}px`);
	}, [windowHeight]);

	const [isSingleVendorAvailable, setIsSingleVendorAvailable] = useState(false);

	useEffect(() => {
		if (thresholdVendors.length === 1) {
			setSelectedVendor(thresholdVendors[0]);
			setIsSingleVendorAvailable(true);
		}
	}, [thresholdVendors]);

	return (
		<div className={styles.thresholdManagementContainer}>
			{isLoadingThreshold && <SpinnerOverlay />}
			<Modal
				isOpen={(!!pendingTechnology || !!pendingVendor)}
				onClose={onCloseHandler}
			>
				<Confirm
					onClose={onCloseHandler}
					onSubmit={onSubmitHandler}
					question='Are you sure you want to leave the form without saving?'
				>
					The changes won’t be saved.
				</Confirm>
			</Modal>
			<PageTitle
				icon={Icons.thresholdManagementInverse()}
				title={EAdminPanelPageTitles.KPIThresholds}
			/>
			<div className={styles.whiteWrapper}>
				{!isSingleVendorAvailable && (
					<div className={styles.vendor}>
						<Select
							handleChange={setCurrentVendor}
							isWithoutUseForms
							label='Vendor'
							name='vendor'
							options={thresholdVendors}
							placeholder='Select vendor'
							value={selectedVendor}
						/>
					</div>
				)}
				{currentTechnology
					&& <ProfileTabSection
						contentClassName={styles.tabContainer}
						currentTab={currentTechnology}
						getCurrentTab={getCurrentTab}
						isWithoutShadow
						maxHeight={maxHeight}
						maxWidth='none'
						setCurrentTab={setCurrentTab}
						tabWidth='220px'
						tabs={technologyTabs}
					/>
				}
			</div>
		</div>
	);
};

export default KpiThresholdManagement;
